import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseBusinessIcon, GraduationCapIcon, HouseIcon, LandmarkIcon, LifeBuoyIcon, StoreIcon } from 'lucide-react';
import { IdentityCredential } from './IdentityCredential';

/**
 * "One account. Everywhere you go."
 *
 * The credential sits at the centre with the six surfaces it reaches arranged
 * either side. The connecting lines are drawn, not implied — the point of the
 * section is that these are one account rather than six products, and the
 * composition has to say that before the copy does.
 */
const LEFT = [{
  icon: GraduationCapIcon,
  label: 'Academics',
  note: 'Courses, results, timetable & more',
  to: '/academics'
}, {
  icon: HouseIcon,
  label: 'Housing',
  note: 'Find, apply, manage accommodation',
  to: '/housing'
}, {
  icon: StoreIcon,
  label: 'Marketplace',
  note: 'Buy, sell, discover and earn',
  to: '/market'
}];
const RIGHT = [{
  icon: LandmarkIcon,
  label: 'Campus',
  note: 'Events, news, communities',
  to: '/community'
}, {
  icon: BriefcaseBusinessIcon,
  label: 'Opportunities',
  note: 'Internships, jobs & scholarships',
  to: '/opportunities'
}, {
  icon: LifeBuoyIcon,
  label: 'Services',
  note: 'Payments, support, transport & more',
  to: '/services'
}];
function SurfaceRow({
  icon: Icon,
  label,
  note,
  to,
  align
}: {
  icon: typeof GraduationCapIcon;
  label: string;
  note: string;
  to: string;
  align: 'left' | 'right';
}) {
  return <li data-id="element-2530" data-mp="12h8vkqkna93l">
      <Link to={to} className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 ease-out hover:bg-white/10 ${align === 'right' ? 'lg:flex-row-reverse lg:text-right' : ''}`} data-id="element-2531" data-mp="12gz2uv87tnu7">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-brand-200" data-id="element-2532" data-mp="17hddml17bbvx">
          <Icon aria-hidden className="h-5 w-5" data-id="element-2533" data-mp="jqr56snabple" />
        </span>
        <span className="min-w-0" data-id="element-2534" data-mp="espjdzyh5c68">
          <span className="block text-body-sm font-semibold text-white" data-id="element-2535" data-mp="ero9hyt72vq3">{label}</span>
          <span className="block text-caption leading-5 text-white/60" data-id="element-2536" data-mp="2evqicfki4e6h">{note}</span>
        </span>
      </Link>
    </li>;
}
export function OneAccount() {
  return <section className="mx-auto max-w-[88rem] px-4 pb-14 sm:px-8" data-id="element-2537" data-mp="2omuw5byfxszq">
      <div className="relative overflow-hidden rounded-panel bg-brand-900 px-5 py-12 sm:px-10 lg:py-16" data-id="element-2538" data-mp="2tkwnek60syma">
        {/* Connecting field. Drawn faintly so it reads as structure, not pattern. */}
        <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full text-white/10" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-2539" data-mp="20w7mom8vmvb2">
          <path d="M18 30 H50 V50 H82 M18 50 H50 M18 70 H50 V50 M82 30 H50 M82 70 H50" fill="none" stroke="currentColor" strokeWidth="0.3" strokeDasharray="1.5 1.5" data-id="element-2540" data-mp="29wxfri6r67l3" />
        </svg>

        <div className="relative mx-auto mb-10 max-w-3xl text-center lg:mb-12" data-id="element-2541" data-mp="3hck1u2m3ih7o">
          <p className="text-caption font-semibold uppercase tracking-[0.16em] text-brand-200" data-id="element-2542" data-mp="3la9an3kjbfv4">
            Your identity, everywhere
          </p>
          <h2 className="mt-4 text-h1 font-bold leading-[1.1] tracking-tight text-white" data-id="element-2543" data-mp="15ndovulg775c">
            One account.
            <br data-id="element-2544" data-mp="10vlypl67f3fq" />
            Everywhere you go.
          </h2>
          <p className="mx-auto mt-5 max-w-[46ch] text-body leading-relaxed text-white/70" data-id="element-2545" data-mp="vxc3jakhc2u4">
            Your identity connects you to everything that matters on campus — and nothing more than
            each surface actually needs.
          </p>
        </div>

        <div className="relative grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" data-id="element-2546" data-mp="lxl8n7s0blti">
          <ul className="space-y-2" data-id="element-2547" data-mp="hxt8njgoinzr">
            {LEFT.map(item => <SurfaceRow key={item.label} {...item} align="left" data-id="element-2548" data-mp="czhxn5ene7fy" />)}
          </ul>

          <IdentityCredential className="mx-auto w-full max-w-xs" data-id="element-2549" data-mp="2caj445mp4599" />

          <ul className="space-y-2" data-id="element-2550" data-mp="27cr0wfglowpr">
            {RIGHT.map(item => <SurfaceRow key={item.label} {...item} align="right" data-id="element-2551" data-mp="27amxrzqj01qj" />)}
          </ul>
        </div>

        <div className="relative mt-10 text-center" data-id="element-2552" data-mp="190of2139fhn3">
          <Link to="/identity" className="group inline-flex min-h-touch items-center gap-2 text-body font-semibold text-white" data-id="element-2553" data-mp="18zdf6ccxqu19">
            How identity works
            <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2554" data-mp="zynjd8nod3nj" />
          </Link>
        </div>
      </div>
    </section>;
}